/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / SETTINGS-MAIL-MODAL — Preview/Edit-Modal pro Mail-Kind
   =============================================================================
   S225 CUT A. Ein Klick auf eine Zeile der Vorlagen-Übersicht öffnet dieses
   Modal (createDarkOverlay + sandboxed iframe, Archiv-Popup-Pattern):

     Phase 1 — Vorschau (schreibgeschützt): die Mail wird mit Testdaten
       gerendert (buildMailKindPreviewHtml → helle Papier-Insel im dunklen
       Rahmen, gg-darkmode-invariants).
     Phase 2 — Bearbeiten: „Bearbeiten" schaltet dasselbe Modal frei — Betreff
       + Body an gleicher Position, Platzhalter-Chips per Klick einfügbar.
       EXPLIZITES Speichern/Abbrechen (kein Save-on-blur —
       gg-create-on-accept-only; Abbrechen hinterlässt nichts).

   Override-only-Semantik bleibt: gespeichert wird über die bestehenden
   update*Template-Handler (Wert == Default → Override-Key geleert). Reset läuft
   inline (updateSetting), NICHT über window.resetMailTemplate — das re-rendert
   die ganze Settings-View und würde das Modal wegreißen.

   ESC/Backdrop-Close kommt aus createDarkOverlay (eigener Handler, kein
   S224-#modal-overlay-Doppel).
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';
import { createDarkOverlay } from '../ui/overlay.js';
import { getMailKind, MAIL_KIND_PILLARS } from '../../core/mail-kinds.js';
import {
  resolveMailKindTemplate, isMailKindOverridden, buildMailKindPreviewHtml,
  fillMailPreview, mailPreviewFixture, smoothEmptyName,
} from '../../core/mail-kind-preview.js';
// S225-Politur — treue Vorschau: derselbe Brand-Wrapper wie der echte Versand
// (Edge `wrapBrand` spiegelt diese Logik) → Logo/Header/Card/Footer wie beim
// Kunden. Pure buildMailKindPreviewHtml bleibt Fallback, falls das Wrapping
// (Logo-Fetch etc.) scheitert.
import { wrapBodyWithBrandHtml } from '../../core/branding.js';

const OVERLAY_ID = 'mail-kind-modal-overlay';

// Modul-State: aktuell geöffnetes Kind + zuletzt fokussiertes Editierfeld (für
// die Chip-Einfügung). Überlebt kein Reload — bewusst, Modal ist transient.
let _openKind = null;
let _lastField = 'body';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

/** settingsKey → window-Handler-Name (update*Template). */
const UPDATE_FN_BY_SLOT = {
  emailTemplates:         'updateReservationTemplate',
  emailTemplatesOrder:    'updateOrderTemplate',
  emailTemplatesDelivery: 'updateDeliveryTemplate',
  emailTemplatesExport:   'updateExportTemplate',
  emailTemplatesNewsletter: 'updateNewsletterTemplate',
};

function _pillarBadge(pillar) {
  const p = MAIL_KIND_PILLARS[pillar];
  if (!p) return '';
  return `<span class="badge-soft" style="font-size:.68rem;">${p.icon} ${htmlEscape(p.label)}</span>`;
}

/** Baut das innere Panel-HTML (Preview- oder Edit-Modus) für das offene Kind. */
function _renderModalInner(mode) {
  const meta = getMailKind(_openKind);
  if (!meta) return '';
  const state = getState();
  const settings = state.settings || {};
  const overridden = isMailKindOverridden(settings, _openKind);

  const header = `
    <div style="padding:16px 20px;border-bottom:1px solid var(--border,#2a352e);background:transparent;">
      <div style="display:flex;justify-content:space-between;gap:12px;align-items:start;">
        <div style="min-width:0;">
          <strong style="font-size:1.05rem;color:var(--ink,#e8efe8);display:block;overflow:hidden;text-overflow:ellipsis;">${htmlEscape(meta.label)}</strong>
          <div style="margin-top:6px;display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
            ${_pillarBadge(meta.pillar)}
            <span style="color:var(--ink-soft,#b3c0b6);font-size:.82rem;">${htmlEscape(meta.trigger)}</span>
            ${overridden ? '<span class="badge-soft" style="font-size:.66rem;">angepasst</span>' : ''}
          </div>
        </div>
        <button onclick="closeMailKindModal()" aria-label="Schließen"
                style="flex:0 0 auto;cursor:pointer;border:0;background:transparent;color:var(--ink-soft,#b3c0b6);font-size:1.15rem;line-height:1;padding:4px 8px;border-radius:6px;">✕</button>
      </div>
      ${meta.note ? `<div style="margin-top:10px;color:var(--ink-muted,#8b9890);font-size:.8rem;">ℹ️ ${htmlEscape(meta.note)}</div>` : ''}
    </div>`;

  if (mode === 'edit') {
    const tpl = resolveMailKindTemplate(settings, _openKind);
    const chips = (meta.tokens || []).map(t =>
      `<button type="button" onclick="insertMailToken('${htmlEscape(t)}')"
               style="margin:0 6px 6px 0;cursor:pointer;border:1px solid var(--border,#2a352e);background:rgba(255,255,255,.06);color:var(--ink,#e8efe8);font-size:.8rem;padding:3px 9px;border-radius:6px;font-family:ui-monospace,Menlo,monospace;">${htmlEscape(t)}</button>`
    ).join('');
    return `
      ${header}
      <div style="flex:1;min-height:0;overflow-y:auto;padding:18px 20px;background:var(--bg-elev,#15201b);">
        <div class="field" style="margin-bottom:14px;">
          <label style="color:var(--ink-soft,#b3c0b6);">Betreff</label>
          <input type="text" id="mail-modal-subject" value="${htmlEscape(tpl.subject)}"
                 onfocus="mailModalTrackField('subject')"
                 style="width:100%;">
        </div>
        <div class="field">
          <label style="color:var(--ink-soft,#b3c0b6);">Text</label>
          <textarea id="mail-modal-body" rows="14"
                    onfocus="mailModalTrackField('body')"
                    style="width:100%;font-family:ui-monospace,Menlo,monospace;font-size:.88rem;">${htmlEscape(tpl.body)}</textarea>
        </div>
        <div style="margin-top:10px;">
          <div style="color:var(--ink-muted,#8b9890);font-size:.78rem;margin-bottom:6px;">Platzhalter einfügen (an Cursor-Position):</div>
          ${chips}
        </div>
      </div>
      <div style="padding:14px 20px;border-top:1px solid var(--border,#2a352e);display:flex;gap:10px;justify-content:flex-end;background:transparent;">
        <button type="button" class="btn btn-ghost" onclick="mailModalCancelEdit()">Abbrechen</button>
        <button type="button" class="btn btn-primary" onclick="mailModalSave()">Speichern</button>
      </div>`;
  }

  // Preview-Modus — „Betreff" wie im Postfach über der gerenderten Mail.
  // Positions-Block aus dem ECHTEN Menü (state.menu), kein erfundenes Gericht.
  const fixture = mailPreviewFixture(settings, state.menu);
  const tpl = resolveMailKindTemplate(settings, _openKind);
  const subjectFilled = smoothEmptyName(fillMailPreview(tpl.subject, fixture));
  return `
    ${header}
    <div style="padding:10px 20px;border-bottom:1px solid var(--border,#2a352e);background:transparent;font-size:.85rem;color:var(--ink-soft,#b3c0b6);">
      Betreff: <span style="color:var(--ink,#e8efe8);font-weight:600;">${htmlEscape(subjectFilled) || '(kein Betreff)'}</span>
    </div>
    <div style="flex:1;min-height:0;background:var(--bg-elev,#15201b);">
      <div id="mail-kind-preview-host" style="width:100%;height:54vh;background:var(--bg-elev,#15201b);"></div>
    </div>
    <div style="padding:14px 20px;border-top:1px solid var(--border,#2a352e);display:flex;gap:10px;justify-content:flex-end;align-items:center;background:transparent;">
      <button type="button" class="btn btn-ghost btn-sm" onclick="mailModalReset()" ${overridden ? '' : 'disabled'}
              title="${overridden ? 'Eigenen Text verwerfen und den Standard wiederherstellen' : 'Diese Vorlage nutzt bereits den Standard'}">↩ Auf Standard zurücksetzen</button>
      <button type="button" class="btn btn-primary" onclick="mailModalEnterEdit()">Bearbeiten</button>
    </div>`;
}

/** Rendert das Panel neu (in-place, ohne Overlay-Neubau) für den Modus. */
function _paint(mode) {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  const panel = overlay.querySelector('#mail-kind-modal-panel');
  if (!panel) return;
  panel.innerHTML = _renderModalInner(mode);
  if (mode !== 'edit') _mountPreviewFrame(overlay);
}

/** Hängt das sandboxed Vorschau-iframe ein (srcdoc, kein DOM-Inject → XSS-sicher).
 *  Rendert den ECHTEN Brand-Wrapper (Logo/Header/Card/Footer) mit Testdaten —
 *  identisch zum Kunden-Versand. Async wegen Logo-Einbettung; Fallback auf die
 *  pure Plain-Vorschau, wenn das Wrapping scheitert. */
function _mountPreviewFrame(overlay) {
  const host = overlay.querySelector('#mail-kind-preview-host');
  if (!host) return;
  const state = getState();
  const settings = state.settings || {};
  const menu = state.menu || [];   // echtes Menü für den Positions-Block
  const kind = _openKind;
  host.innerHTML = '';
  const frame = document.createElement('iframe');
  frame.setAttribute('sandbox', '');
  frame.setAttribute('title', 'Mail-Vorschau');
  frame.style.cssText = 'width:100%;height:100%;border:0;background:#efe9dd;';
  host.appendChild(frame);

  const setDoc = (html) => {
    // Race-Guard: nur setzen, wenn dasselbe Kind noch offen + der Frame im DOM ist
    // (Nutzer könnte inzwischen geschlossen/gewechselt/bearbeitet haben).
    if (_openKind === kind && frame.isConnected) frame.srcdoc = html;
  };

  // Testdaten in Betreff+Body → echten Brand-Wrapper drumherum (wie Edge).
  // Positions-Block aus dem ECHTEN Menü (state.menu), kein erfundenes Gericht.
  const tpl = resolveMailKindTemplate(settings, kind);
  const bodyFilled = fillMailPreview(tpl.body, mailPreviewFixture(settings, menu));
  const restaurantName = settings.restaurantName || '';
  Promise.resolve()
    .then(() => wrapBodyWithBrandHtml(bodyFilled, { restaurantName }))
    .then((html) => setDoc(html))
    .catch(() => setDoc(buildMailKindPreviewHtml(settings, kind, menu)));
}

/** Öffnet das Modal im Vorschau-Modus für ein Mail-Kind. */
export function openMailKindModal(kind) {
  if (!getMailKind(kind)) return;
  closeMailKindModal();
  _openKind = kind;
  _lastField = 'body';

  const overlay = createDarkOverlay({ id: OVERLAY_ID, zIndex: 9000, onClose: closeMailKindModal });
  overlay.innerHTML = `
    <div id="mail-kind-modal-panel" role="dialog" aria-modal="true"
         style="background:var(--bg-elev,#15201b);color:var(--ink,#e8efe8);width:min(720px,100%);max-height:90vh;
                display:flex;flex-direction:column;border-radius:12px;box-shadow:0 12px 40px rgba(0,0,0,.5);overflow:hidden;"></div>`;
  document.body.appendChild(overlay);
  _paint('preview');
}

export function closeMailKindModal() {
  const el = document.getElementById(OVERLAY_ID);
  if (el) {
    if (typeof el._ggDetachKey === 'function') el._ggDetachKey();
    if (el.parentNode) el.parentNode.removeChild(el);
  }
  _openKind = null;
}

/** „Bearbeiten" → Edit-Modus (Schreibschutz auf). */
export function mailModalEnterEdit() {
  if (!_openKind) return;
  _lastField = 'body';
  _paint('edit');
  const body = document.getElementById('mail-modal-body');
  if (body) body.focus();
}

/** „Abbrechen" → zurück in die Vorschau, ohne zu speichern. */
export function mailModalCancelEdit() {
  if (!_openKind) return;
  _paint('preview');
}

/** onfocus-Tracker: merkt sich das zuletzt fokussierte Editierfeld. */
export function mailModalTrackField(field) {
  if (field === 'subject' || field === 'body') _lastField = field;
}

/** Fügt einen Platzhalter an der Cursor-Position des zuletzt fokussierten Feldes ein. */
export function insertMailToken(token) {
  const id = _lastField === 'subject' ? 'mail-modal-subject' : 'mail-modal-body';
  const el = document.getElementById(id);
  if (!el) return;
  const start = el.selectionStart != null ? el.selectionStart : el.value.length;
  const end = el.selectionEnd != null ? el.selectionEnd : el.value.length;
  el.value = el.value.slice(0, start) + token + el.value.slice(end);
  const caret = start + token.length;
  el.focus();
  try { el.setSelectionRange(caret, caret); } catch (_) { /* input types ohne Range */ }
}

/** „Speichern" → Betreff + Body über den Override-Handler persistieren
 *  (Wert == Default → Override-Key geleert), dann zurück in die Vorschau. */
export async function mailModalSave() {
  if (!_openKind) return;
  const meta = getMailKind(_openKind);
  const subjEl = document.getElementById('mail-modal-subject');
  const bodyEl = document.getElementById('mail-modal-body');
  if (!meta || !subjEl || !bodyEl) return;
  const fnName = UPDATE_FN_BY_SLOT[meta.settingsKey];
  const fn = fnName && window[fnName];
  if (typeof fn !== 'function') return;
  await fn(meta.settingsField, 'subject', subjEl.value);
  await fn(meta.settingsField, 'body', bodyEl.value);
  if (typeof window.refreshMailKindRow === 'function') window.refreshMailKindRow(_openKind);
  _paint('preview');
}

/** Reset inline (kein window.resetMailTemplate → kein Full-Rerender, Modal bleibt). */
export async function mailModalReset() {
  if (!_openKind) return;
  const meta = getMailKind(_openKind);
  if (!meta) return;
  const state = getState();
  const cur = { ...((state.settings && state.settings[meta.settingsKey]) || {}) };
  if (meta.settingsField in cur) {
    delete cur[meta.settingsField];
    if (typeof window.updateSetting === 'function') {
      await window.updateSetting(meta.settingsKey, cur);
    }
  }
  if (typeof window.refreshMailKindRow === 'function') window.refreshMailKindRow(_openKind);
  _paint('preview');
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    openMailKindModal,
    closeMailKindModal,
    mailModalEnterEdit,
    mailModalCancelEdit,
    mailModalTrackField,
    insertMailToken,
    mailModalSave,
    mailModalReset,
  });
}
